import React from 'react';
import { DefaultButton, Modal, PrimaryButton } from '@fluentui/react';
import { apiClient } from '../Services/api-client';
import styles from './CreateCommunityModal.module.css';

interface CommunityFormState {
  friendlyName: string;
  address: string;
  contact: { email: string; phone: string };
}

interface CreateCommunityModalProps {
  isOpen: boolean;
  portfolioId: string;
  portfolioName?: string;
  existingCommunity?: any;
  onDismiss: () => void;
  onSaved: (community: any, isEdit: boolean) => void;
  onError: (message: string) => void;
}

export const CreateCommunityModal: React.FC<CreateCommunityModalProps> = ({
  isOpen,
  portfolioId,
  portfolioName,
  existingCommunity,
  onDismiss,
  onSaved,
  onError,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [formData, setFormData] = React.useState<CommunityFormState>({
    friendlyName: '',
    address: '',
    contact: { email: '', phone: '' },
  });

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (existingCommunity) {
      setFormData({
        friendlyName: existingCommunity.friendlyName || existingCommunity.displayName || '',
        address: existingCommunity.address || '',
        contact: {
          email: existingCommunity.contact?.email || existingCommunity.contactEmail || '',
          phone: existingCommunity.contact?.phone || existingCommunity.contactPhone || '',
        },
      });
    } else {
      setFormData({
        friendlyName: '',
        address: '',
        contact: { email: '', phone: '' },
      });
    }
  }, [isOpen, existingCommunity]);

  const isValid =
    Boolean(formData.friendlyName.trim()) &&
    Boolean(formData.address.trim()) &&
    Boolean(formData.contact.email.trim()) &&
    Boolean(formData.contact.phone.trim());

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!portfolioId) {
      onError('Select a portfolio before creating a community.');
      return;
    }

    if (!isValid) {
      onError('Complete all required fields to continue.');
      return;
    }

    const isEdit = Boolean(existingCommunity?.communityId);
    const url = isEdit
      ? `/portfolios/${portfolioId}/communities/${existingCommunity.communityId}`
      : `/portfolios/${portfolioId}/communities`;
    const method = isEdit ? 'PUT' : 'POST';
    const payload = {
      displayName: formData.friendlyName.trim(),
      address: formData.address.trim(),
      contact: {
        email: formData.contact.email.trim(),
        phone: formData.contact.phone.trim(),
      },
    };

    try {
      setLoading(true);
      const response = await apiClient.request({
        url,
        method,
        data: payload,
        headers: { 'Content-Type': 'application/json' },
      });

      const community = response.data;
      onSaved(community, isEdit);
      onDismiss();
    } catch (error: any) {
      onError(error?.response?.data?.error || error?.message || 'Failed to save community');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      isBlocking={false}
      containerClassName={styles.modal}
    >
      <div className={styles.header}>
        <h2>{existingCommunity ? 'Update Community' : 'Create Community'}</h2>
        <p>
          {portfolioName ? `Portfolio: ${portfolioName}` : 'Assign this community to a portfolio.'}
        </p>
      </div>
      <form onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label>Community Name *</label>
          <input
            type="text"
            value={formData.friendlyName}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, friendlyName: event.target.value }))
            }
            placeholder="e.g., Crescendo Apartments"
            required
          />
        </div>
        <div className={styles.formGroup}>
          <label>Address *</label>
          <input
            type="text"
            value={formData.address}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, address: event.target.value }))
            }
            placeholder="123 Main Street, Anytown, USA"
            required
          />
        </div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>Email *</label>
            <input
              type="email"
              value={formData.contact.email}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  contact: { ...prev.contact, email: event.target.value },
                }))
              }
              placeholder="community@example.com"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label>Phone *</label>
            <input
              type="tel"
              value={formData.contact.phone}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  contact: { ...prev.contact, phone: event.target.value },
                }))
              }
              placeholder="+1 (555) 123-4567"
              required
            />
          </div>
        </div>
        <div className={styles.actions}>
          <PrimaryButton
            type="submit"
            text={loading ? 'Saving...' : existingCommunity ? 'Update' : 'Create'}
            disabled={!isValid || loading}
          />
          <DefaultButton text="Cancel" onClick={onDismiss} disabled={loading} />
        </div>
      </form>
    </Modal>
  );
};
